import React, { useState, useEffect, useCallback } from 'react';
import Menu from '../menu';
import {
  getConfig,
  fetchConfigFromServer,
  saveConfigToServer,
  resetConfigOnServer,
} from '../../utils/classificarMensagem';

const GRUPOS = [
  {
    key: 'alerta',
    label: 'Alerta — Moderado',
    badgeClass: 'bg-warning text-dark',
    alertClass: 'alert-warning',
    descricao: 'Mensagens que contiverem qualquer um desses textos serão classificadas como Alerta.',
  },
  {
    key: 'aviso',
    label: 'Aviso — Normal',
    badgeClass: 'bg-success',
    alertClass: 'alert-success',
    descricao: 'Mensagens que contiverem qualquer um desses textos serão classificadas como Aviso.',
  },
];

const ClassificacaoConfig = () => {
  const [config, setConfig] = useState(() => getConfig());
  const [inputs, setInputs] = useState({ alerta: '', aviso: '' });
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigFromServer()
      .then(setConfig)
      .catch(() => {/* mantém cache localStorage */})
      .finally(() => setLoading(false));
  }, []);

  const saveConfig = useCallback(async (newConfig) => {
    setStatus('saving');
    try {
      await saveConfigToServer(newConfig);
      setStatus('saved');
      setTimeout(() => setStatus(null), 2000);
    } catch {
      setStatus('error');
    }
  }, []);

  const addString = useCallback((grupo) => {
    const val = inputs[grupo].trim();
    if (!val || config[grupo].includes(val)) return;
    const newConfig = { ...config, [grupo]: [...config[grupo], val] };
    setConfig(newConfig);
    setInputs(prev => ({ ...prev, [grupo]: '' }));
    saveConfig(newConfig);
  }, [config, inputs, saveConfig]);

  const removeString = useCallback((grupo, idx) => {
    const newConfig = { ...config, [grupo]: config[grupo].filter((_, i) => i !== idx) };
    setConfig(newConfig);
    saveConfig(newConfig);
  }, [config, saveConfig]);

  const handleReset = useCallback(async () => {
    if (!window.confirm('Restaurar os valores padrão? As alterações serão perdidas.')) return;
    setStatus('saving');
    try {
      const fresh = await resetConfigOnServer();
      setConfig(fresh);
      setStatus(null);
    } catch {
      setStatus('error');
    }
  }, []);

  const handleKeyDown = useCallback((e, grupo) => {
    if (e.key === 'Enter') { e.preventDefault(); addString(grupo); }
  }, [addString]);

  return (
    <>
      <Menu onSidebarToggle={() => {}} />
      <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="mb-0">Gestão de Classificação de Mensagens</h5>
        <div className="d-flex align-items-center gap-2">
          {status === 'saving' && <small className="text-muted">Salvando...</small>}
          {status === 'saved'  && <small className="text-success">Salvo ✓</small>}
          {status === 'error'  && <small className="text-danger">Erro ao salvar</small>}
          <button className="btn btn-sm btn-outline-secondary" onClick={handleReset} disabled={status === 'saving'}>
            Restaurar Padrão
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted small">Carregando configuração do servidor...</div>
      ) : (
        <>
          <div className="alert alert-secondary py-2 mb-4">
            <small>
              <strong>Exceção — Grave</strong> é o nível padrão aplicado a mensagens que não
              correspondem a nenhum grupo abaixo. A verificação usa <em>texto contém string</em> (case-sensitive).
            </small>
          </div>

          <div className="row g-4">
            {GRUPOS.map(({ key, label, badgeClass, alertClass, descricao }) => (
              <div className="col-12 col-md-6" key={key}>
                <div className="card border-0 shadow-sm">
                  <div className="card-header d-flex align-items-center gap-2">
                    <span className={`badge ${badgeClass}`}>{label}</span>
                    <small className="text-muted ms-1">{config[key].length} strings</small>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small mb-3">{descricao}</p>

                    <div className="input-group input-group-sm mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Adicionar string..."
                        value={inputs[key]}
                        onChange={e => { const v = e.target.value; setInputs(prev => ({ ...prev, [key]: v })); }}
                        onKeyDown={e => handleKeyDown(e, key)}
                      />
                      <button className="btn btn-outline-primary" onClick={() => addString(key)}>
                        Adicionar
                      </button>
                    </div>

                    <div className={`alert ${alertClass} p-2`} style={{ minHeight: '60px' }}>
                      {config[key].length === 0 ? (
                        <small className="text-muted fst-italic">Nenhuma string cadastrada.</small>
                      ) : (
                        <ul className="list-unstyled mb-0">
                          {config[key].map((str, idx) => (
                            <li key={idx} className="d-flex align-items-center justify-content-between py-1 border-bottom border-opacity-25">
                              <code className="small">{str}</code>
                              <button
                                className="btn btn-sm btn-link text-danger p-0 ms-2"
                                title="Remover"
                                onClick={() => removeString(key, idx)}
                              >
                                &times;
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default ClassificacaoConfig;
